import { yupResolver } from "@hookform/resolvers/yup";
import { Check, Copy, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@krea/shared/design-system/alerts/Alert";
import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldDescription } from "@krea/shared/design-system/input/field";
import { Input } from "@krea/shared/design-system/input/input";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { useGetCurrentLenderCompanyQuery } from "../hooks/use-get-lender-company.query";
import { useUpdateLenderCompanyMutation } from "../hooks/use-update-lender-company.mutation";

const schema = yup.object().shape({
  integrationEmail: yup.string().email("Invalid email").nonNullable(),
});

type IApiIntegrationSectionFormValues = yup.InferType<typeof schema>;

export const ApiIntegrationSection = () => {
  const [copied, setCopied] = useState<boolean>(false);

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutate: updateLenderCompany } = useUpdateLenderCompanyMutation();

  const onCopy = useCallback(async (): Promise<void> => {
    if (!lenderCompany) return;

    await navigator.clipboard.writeText(lenderCompany.clientId);
    setCopied(true);
    toast.success("Client ID copied to clipboard");
    window.setTimeout(() => setCopied(false), 1_500);
  }, [lenderCompany]);

  const form = useForm<IApiIntegrationSectionFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      integrationEmail: "",
    },
  });

  useEffect(() => {
    if (lenderCompany) {
      const initialValues: IApiIntegrationSectionFormValues = {};

      if (lenderCompany.integrationEmail) {
        initialValues.integrationEmail = lenderCompany.integrationEmail;
      }
      form.reset(initialValues);
    }
  }, [lenderCompany, form]);

  const handleSubmit = useCallback(() => {
    if (!lenderCompany) return;
    form.handleSubmit((data) => {
      updateLenderCompany(
        { lenderCompanyId: lenderCompany.id, data },
        {
          onSuccess: () => {
            toast.success("API integration updated successfully");
          },
          onError: () => {
            toast.error("Failed to update API integration");
          },
        },
      );
    })();
  }, [form, updateLenderCompany, lenderCompany]);

  return (
    <Card>
      <CardContent className="space-y-6">
        <div>
          <h1 className="text-section">API Integration</h1>
          <p className="text-base">
            Configure the API integration to receive notifications about events
            in your account.
          </p>
        </div>
        <Separator />
        <Alert
          variant="warning"
          leftElement={<TriangleAlert className="mr-4 size-8 text-orange" />}
        >
          <AlertTitle className="text-base-strong text-orange">
            Keep your credentials secure
          </AlertTitle>
          <AlertDescription className="text-helper">
            Never share your Client ID or API keys publicly. Store them securely
            in your environment variables. If you suspect a security breach,
            please contact us immediately.
          </AlertDescription>
        </Alert>

        <div>
          <label htmlFor="client-id" className="mb-2 block text-label">
            Client ID
          </label>

          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Input value={lenderCompany?.clientId} readOnly />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onCopy}
              aria-label="Copy Client ID"
              className="text-primary outline-hidden ring-0 focus-visible:ring-[none]"
            >
              {copied ? (
                <Check size={16} strokeWidth={3} />
              ) : (
                <Copy size={16} strokeWidth={3} />
              )}
            </Button>
          </div>

          <p className="mt-2 text-helper">
            Use this unique identifier to authenticate API requests.
          </p>
        </div>

        <ControlledWrapper
          control={form.control}
          name="integrationEmail"
          label="Integration Email"
          render={({ field }) => (
            <>
              <Input
                type="email"
                placeholder="integrations@your-company.com"
                {...field}
                disabled={form.formState.isSubmitting}
              />
              <FieldDescription>
                Email address for integration-related notifications and alerts.
              </FieldDescription>
            </>
          )}
        />

        <Separator />
        <div className="flex justify-end">
          <Button
            type="button"
            size="lg"
            onClick={handleSubmit}
            disabled={
              form.formState.isSubmitting ||
              !form.formState.isValid ||
              !form.formState.isDirty
            }
          >
            Save Configuration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
